import React from 'react';
import { cn } from '../../../utils/cn';
import {
  ALL_CONTACTS,
  getActiveMarkers,
  getCenterContactForTooth,
  getIprContactValue,
  IPR_TOOL_SELECT,
  LOWER_TEETH,
  TOOTH_MARKERS,
  UPPER_TEETH,
} from '../../../lib/iprToothData';
import ToothMarkerToolbar from './ToothMarkerToolbar';

const DentalDiagram = ({
  iprData,
  selectedTooth,
  onToothSelect,
  readOnly = false,
  variant = 'default',
  activeTool = IPR_TOOL_SELECT,
  onToolChange,
  onToggleMarker,
}) => {
  const compact = variant === 'compact';
  const isPaintMode = !readOnly && activeTool !== IPR_TOOL_SELECT;
  const selectedContact = getCenterContactForTooth(selectedTooth);

  const contactForPair = (left, right) =>
    ALL_CONTACTS.find(
      (contact) => contact.left === left && contact.right === right
    );

  const toothHasIpr = (number) =>
    ALL_CONTACTS.some(
      (contact) =>
        (contact.left === number || contact.right === number) &&
        getIprContactValue(iprData, contact) > 0
    );

  const handleToothClick = (number) => {
    if (readOnly) return;
    if (isPaintMode && typeof onToggleMarker === 'function') {
      onToggleMarker(number, activeTool);
      return;
    }
    onToothSelect?.(number);
  };

  const ToothComponent = ({ number, isUpper }) => {
    const tooth = iprData?.[number];
    const toothContact = getCenterContactForTooth(number);
    const isSelected =
      !isPaintMode &&
      selectedContact &&
      (selectedContact.left === number || selectedContact.right === number);
    const isMissing = Boolean(tooth?.missing);
    const hasReduction = toothHasIpr(number);
    const markers = getActiveMarkers(tooth).filter((marker) => marker.key !== 'missing');
    const toothSize = compact ? 'w-8 h-10' : 'w-10 h-12';
    const toothMinW = compact ? 'min-w-[2.15rem]' : 'min-w-[2.65rem]';

    let fill = hasReduction ? 'rgb(236 253 245)' : 'white';
    let stroke = hasReduction ? 'rgb(13 148 136)' : 'rgb(148 163 184)';
    if (isMissing) {
      fill = 'rgb(226 232 240)';
      stroke = 'rgb(100 116 139)';
    }

    const content = (
      <div className={cn('ipr-tooth-glyph relative transition-all print:h-10 print:w-8', toothSize)}>
        <svg viewBox="0 0 64 76" className={cn('ipr-tooth-glyph print:h-10 print:w-8', toothSize, isMissing && 'opacity-70')}>
          <path
            d="M8 14C10 7 18 4 26 6C29 6.8 31 8.2 32 9.5C33 8.2 35 6.8 38 6C46 4 54 7 56 14C58 20 56 30 51 35C48 38 46 42 45 46C44 51 42 56 39 59C37 61 35 63 32 66C29 63 27 61 25 59C22 56 20 51 19 46C18 42 16 38 13 35C8 30 6 20 8 14Z"
            fill={fill}
            stroke={stroke}
            strokeWidth="2.2"
            strokeDasharray={isMissing ? '3 2' : undefined}
          />
          {!isMissing && (
            <>
              <path d="M24 58C26 54 28 49 29 44" stroke="rgb(203 213 225)" strokeWidth="1.1" fill="none" />
              <path d="M40 58C38 54 36 49 35 44" stroke="rgb(203 213 225)" strokeWidth="1.1" fill="none" />
            </>
          )}
          {isMissing && (
            <path
              d="M18 22 L46 54 M46 22 L18 54"
              stroke="rgb(71 85 105)"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          )}
        </svg>
        {markers.length > 0 && (
          <div className="pointer-events-none absolute -bottom-0.5 left-1/2 flex -translate-x-1/2 gap-0.5">
            {markers.map((marker) => (
              <span
                key={marker.key}
                className={cn(
                  'inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded px-0.5 text-[8px] font-bold leading-none',
                  marker.badgeClass
                )}
                title={marker.label}
              >
                {marker.shortLabel}
              </span>
            ))}
          </div>
        )}
      </div>
    );

    return (
      <div className={cn('ipr-tooth-cell flex flex-col items-center print:min-w-[2.15rem]', toothMinW)}>
        {isUpper && (
          <div className={cn('mb-0.5 font-medium text-gray-500', compact ? 'text-[10px]' : 'text-xs')}>
            {number}
          </div>
        )}
        {readOnly ? (
          content
        ) : (
          <button
            type="button"
            onClick={() => handleToothClick(number)}
            aria-label={
              isPaintMode
                ? `Basculer ${TOOTH_MARKERS.find((marker) => marker.key === activeTool)?.label || 'marqueur'} sur dent ${number}`
                : `Sélectionner le contact ${toothContact?.label || number}`
            }
            className={cn(
              'relative cursor-pointer rounded-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              isSelected && 'ring-2 ring-primary/70 ring-offset-1',
              isPaintMode && 'hover:scale-105',
              isMissing && !isPaintMode && 'opacity-90'
            )}
          >
            {content}
          </button>
        )}
        {!isUpper && (
          <div className={cn('mt-0.5 font-medium text-gray-500', compact ? 'text-[10px]' : 'text-xs')}>
            {number}
          </div>
        )}
      </div>
    );
  };

  const ContactSlot = ({ contact }) => {
    const value = getIprContactValue(iprData, contact);
    const selected = selectedContact?.id === contact.id && !isPaintMode;
    const disabled = Boolean(
      iprData?.[contact.left]?.missing || iprData?.[contact.right]?.missing
    );

    return (
      <div
        className={cn(
          'ipr-contact-slot relative flex w-3 shrink-0 items-center justify-center self-stretch rounded-sm print:w-2',
          selected && 'ipr-contact-selected bg-primary/10 ring-2 ring-primary/50 print:bg-transparent print:ring-0',
          disabled && 'opacity-30'
        )}
        aria-hidden="true"
      >
        {value > 0 && !disabled && (
          <>
            <span className="h-8 w-1 rounded bg-red-600" />
            <span className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-0.5 text-[9px] font-bold leading-tight text-red-700 shadow-sm">
              {value.toFixed(2)}
            </span>
          </>
        )}
        {selected && value === 0 && !disabled && (
          <span className="ipr-pdf-hide print:hidden h-8 w-1 rounded bg-red-600" />
        )}
      </div>
    );
  };

  const DentalArch = ({ teeth, isUpper }) => (
    <div className="ipr-dental-arch overflow-x-auto pb-1 print:overflow-visible">
      <div className="mx-auto flex min-w-max items-center justify-center">
        {teeth.map((tooth, index) => {
          const next = teeth[index + 1];
          const contact = next ? contactForPair(tooth, next) : null;
          return (
            <React.Fragment key={tooth}>
              <ToothComponent number={tooth} isUpper={isUpper} />
              {contact && <ContactSlot contact={contact} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={compact ? 'p-3 sm:p-4' : 'border-b border-gray-200/80 p-6 md:p-8'}>
      <h3 className={compact ? 'mb-3 px-1 text-sm font-semibold text-foreground' : 'mb-4 text-lg font-semibold text-gray-900'}>
        Diagramme dentaire (notation ISO)
      </h3>

      {!readOnly && typeof onToolChange === 'function' && (
        <div className="ipr-pdf-hide print:hidden">
          <ToothMarkerToolbar activeTool={activeTool} onToolChange={onToolChange} />
        </div>
      )}

      <div className={compact ? 'rounded-xl border border-border bg-muted/20 p-3 sm:p-4' : 'ipr-section-surface p-5 md:p-6'}>
        <div className={compact ? 'mb-5' : 'mb-8'}>
          <div className={compact ? 'mb-2 text-center text-xs font-semibold text-muted-foreground' : 'mb-3 text-center text-sm font-semibold tracking-wide text-gray-700'}>
            Arcade supérieure
          </div>
          <DentalArch teeth={UPPER_TEETH} isUpper />
        </div>
        <div className={`border-t border-dashed ${compact ? 'my-4 border-border' : 'my-6 border-t-2 border-gray-300'}`} />
        <div>
          <div className={compact ? 'mb-2 text-center text-xs font-semibold text-muted-foreground' : 'mb-3 text-center text-sm font-semibold tracking-wide text-gray-700'}>
            Arcade inférieure
          </div>
          <DentalArch teeth={LOWER_TEETH} isUpper={false} />
        </div>
      </div>

      <div className={compact ? 'mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[11px] text-muted-foreground' : 'mt-4 flex flex-wrap items-center justify-center gap-3 text-sm md:gap-5'}>
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="h-3.5 w-3.5 shrink-0 rounded border-2 border-primary bg-primary/10" />
          <span className={compact ? 'truncate' : 'text-gray-600'}>IPR requis</span>
        </div>
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="h-3.5 w-3.5 shrink-0 rounded border-2 border-dashed border-slate-500 bg-slate-200" />
          <span className={compact ? 'truncate' : 'text-gray-600'}>Manquante (M)</span>
        </div>
        {TOOTH_MARKERS.filter((marker) => marker.key !== 'missing').map((marker) => (
          <div key={marker.key} className="flex min-w-0 items-center gap-1.5">
            <span className={cn('inline-flex h-4 min-w-[1rem] items-center justify-center rounded px-0.5 text-[9px] font-bold', marker.badgeClass)}>
              {marker.shortLabel}
            </span>
            <span className={compact ? 'truncate' : 'text-gray-600'}>{marker.label}</span>
          </div>
        ))}
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="h-3.5 w-1 shrink-0 rounded bg-red-600" />
          <span className={compact ? 'truncate' : 'text-gray-600'}>Contact IPR (mm)</span>
        </div>
      </div>

    </div>
  );
};

export default DentalDiagram;
